// src/components/JainSpiritualMatrimony.jsx

import React, { useEffect } from "react";
import { FaPrayingHands, FaHeart, FaBalanceScale } from "react-icons/fa";
import jainLogo from "../../assets/jainlogos.png";

/* ─── CSS ─────────────────────────────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;0,700;1,600&family=Inter:wght@400;500;600&display=swap');

.jbsm {
  position: relative;
  width: 100%;
  overflow: hidden;
  background: linear-gradient(135deg, #1A0505 0%, #3D0A0A 55%, #5A1010 100%);
  font-family: 'Inter', sans-serif;
}

/* gold top accent */
.jbsm::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0; height: 3px;
  background: linear-gradient(90deg, transparent, #C9962A 30%, #E8C46A 50%, #C9962A 70%, transparent);
  z-index: 2;
}
/* gold bottom accent */
.jbsm::after {
  content: '';
  position: absolute;
  bottom: 0; left: 0; right: 0; height: 3px;
  background: linear-gradient(90deg, transparent, #C9962A 30%, #E8C46A 50%, #C9962A 70%, transparent);
  z-index: 2;
}

/* dot-grid watermark */
.jbsm-dust {
  position: absolute; inset: 0; pointer-events: none; z-index: 0;
  background-image: radial-gradient(circle, rgba(201,150,42,0.13) 1px, transparent 1px);
  background-size: 36px 36px;
}

/* spinning mandala — far right */
.jbsm-mandala {
  position: absolute;
  right: -100px; top: 50%;
  transform: translateY(-50%);
  width: min(52%, 420px);
  aspect-ratio: 1;
  opacity: 0.055;
  pointer-events: none;
  animation: jbsmSpin 90s linear infinite;
}
@keyframes jbsmSpin { to { transform: translateY(-50%) rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .jbsm-mandala { animation: none; } }

/* ── inner ── */
.jbsm-inner {
  position: relative; z-index: 1;
  max-width: 1160px; margin: 0 auto;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: clamp(24px, 4vw, 56px);
  padding: clamp(40px, 6vw, 80px) clamp(20px, 5vw, 56px);
}

/* ══ LEFT ══ */
.jbsm-left {
  flex: 1 1 56%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: clamp(18px, 2.5vw, 26px);
}

/* brand row */
.jbsm-brand {
  display: flex;
  align-items: center;
  gap: clamp(10px, 1.5vw, 14px);
}
.jbsm-logo-ring {
  width: clamp(44px, 5.5vw, 58px);
  height: clamp(44px, 5.5vw, 58px);
  border-radius: 50%;
  border: 2px solid #C9962A;
  box-shadow: 0 0 0 4px rgba(201,150,42,0.15);
  object-fit: cover;
  flex-shrink: 0;
}
.jbsm-brand-text { display: flex; flex-direction: column; line-height: 1.25; }
.jbsm-brand-name {
  font-family: 'Playfair Display', serif;
  font-size: clamp(15px, 1.7vw, 19px);
  font-weight: 700;
  color: #FDF6EC;
}
.jbsm-brand-tag {
  font-size: clamp(9.5px, 1vw, 11px);
  color: #C9962A;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  font-weight: 600;
}

/* eyebrow */
.jbsm-eyebrow {
  display: inline-flex; align-items: center; gap: 10px;
  font-size: clamp(10px, 1.1vw, 12px);
  font-weight: 600; letter-spacing: 2.2px; text-transform: uppercase;
  color: #C9962A;
}
.jbsm-eline { width: 28px; height: 1px; background: linear-gradient(90deg, transparent, #C9962A); }
.jbsm-eline.r { background: linear-gradient(90deg, #C9962A, transparent); }

/* headline */
.jbsm-headline {
  font-family: 'Playfair Display', serif;
  font-size: clamp(26px, 4vw, 46px);
  font-weight: 700;
  color: #FDF6EC;
  line-height: 1.2;
  letter-spacing: -0.4px;
}
.jbsm-headline em { font-style: italic; color: #E8C46A; }

/* quote */
.jbsm-quote {
  border-left: 3px solid rgba(201,150,42,0.5);
  padding: clamp(10px,1.5vw,14px) clamp(14px,2vw,20px);
  background: rgba(201,150,42,0.07);
  border-radius: 0 8px 8px 0;
}
.jbsm-quote p {
  font-family: 'Playfair Display', serif;
  font-style: italic;
  font-size: clamp(13px, 1.4vw, 15px);
  color: rgba(253,246,236,0.82);
  line-height: 1.65;
}
.jbsm-quote cite {
  display: block;
  margin-top: 6px;
  font-size: 11px;
  color: #C9962A;
  font-style: normal;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  font-weight: 600;
}

/* features */
.jbsm-features {
  display: flex;
  flex-direction: column;
  gap: clamp(10px, 1.5vw, 14px);
}

.jbsm-feat {
  display: flex;
  align-items: flex-start;
  gap: clamp(12px, 1.5vw, 16px);
  background: rgba(201,150,42,0.07);
  border: 1px solid rgba(201,150,42,0.2);
  border-radius: 12px;
  padding: clamp(14px, 2vw, 18px) clamp(16px, 2vw, 20px);
  transition: border-color 0.2s, background 0.2s;
}
.jbsm-feat:hover {
  border-color: rgba(201,150,42,0.5);
  background: rgba(201,150,42,0.11);
}
.jbsm-feat-icon {
  width: clamp(40px, 4.5vw, 48px);
  height: clamp(40px, 4.5vw, 48px);
  border-radius: 50%;
  background: rgba(201,150,42,0.13);
  border: 1px solid rgba(201,150,42,0.35);
  display: flex; align-items: center; justify-content: center;
  color: #C9962A;
  font-size: clamp(17px, 2vw, 21px);
  flex-shrink: 0;
}
.jbsm-feat-body { flex: 1; min-width: 0; }
.jbsm-feat-title {
  font-family: 'Playfair Display', serif;
  font-size: clamp(14px, 1.5vw, 16px);
  font-weight: 700;
  color: #FDF6EC;
  margin-bottom: 4px;
}
.jbsm-feat-desc {
  font-size: clamp(12px, 1.2vw, 13.5px);
  color: rgba(253,246,236,0.68);
  line-height: 1.6;
}

/* CTA */
.jbsm-cta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: clamp(10px,1.4vw,13px) clamp(24px,2.8vw,36px);
  background: linear-gradient(90deg, #C9962A, #A87820);
  border: none; border-radius: 32px;
  color: #2C1A0E;
  font-size: clamp(13px, 1.4vw, 15px);
  font-weight: 700;
  font-family: 'Playfair Display', serif;
  cursor: pointer; text-decoration: none;
  box-shadow: 0 4px 20px rgba(201,150,42,0.3);
  transition: opacity 0.18s, transform 0.15s;
  align-self: flex-start;
}
.jbsm-cta:hover { opacity: 0.87; transform: translateY(-1px); }

/* ══ RIGHT ══ */
.jbsm-right {
  flex: 0 0 36%;
  max-width: 340px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: clamp(16px, 2vw, 20px);
}

/* dharma quote card */
.jbsm-dharma-card {
  background: rgba(201,150,42,0.07);
  border: 1px solid rgba(201,150,42,0.25);
  border-radius: 16px;
  padding: clamp(22px, 3vw, 32px) clamp(18px, 2.5vw, 26px);
  text-align: center;
  position: relative;
  overflow: hidden;
}
.jbsm-dharma-om {
  font-size: clamp(36px, 5vw, 52px);
  color: #C9962A;
  font-family: serif;
  line-height: 1;
  margin-bottom: 14px;
  filter: drop-shadow(0 0 10px rgba(201,150,42,0.3));
}
.jbsm-dharma-text {
  font-family: 'Playfair Display', serif;
  font-style: italic;
  font-size: clamp(13px, 1.4vw, 15px);
  color: rgba(253,246,236,0.85);
  line-height: 1.75;
}
.jbsm-dharma-attr {
  display: block;
  margin-top: 14px;
  font-size: 11px;
  color: #C9962A;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  font-weight: 600;
}

/* stat pills on right */
.jbsm-stats {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.jbsm-stat {
  display: flex;
  align-items: center;
  gap: 14px;
  background: rgba(201,150,42,0.08);
  border: 1px solid rgba(201,150,42,0.2);
  border-radius: 10px;
  padding: 12px 16px;
}
.jbsm-stat-num {
  font-family: 'Playfair Display', serif;
  font-size: clamp(22px, 2.5vw, 28px);
  font-weight: 700;
  color: #E8C46A;
  line-height: 1;
  white-space: nowrap;
}
.jbsm-stat-lbl {
  font-size: clamp(11px, 1.1vw, 12.5px);
  color: rgba(253,246,236,0.68);
  line-height: 1.4;
  font-weight: 500;
}

/* divider row */
.jbsm-divrow {
  display: flex; align-items: center; gap: 10px;
}
.jbsm-divline { flex: 1; height: 1px; background: rgba(201,150,42,0.3); }
.jbsm-divsym  { color: #C9962A; font-size: 14px; }

/* ══ BREAKPOINTS ══ */
@media (max-width: 900px) {
  .jbsm-right { display: none; }
  .jbsm-left  { flex: 1 1 100%; }
}
@media (max-width: 560px) {
  .jbsm-inner { padding: 32px 16px; }
  .jbsm-cta   { align-self: stretch; justify-content: center; }
}
@media (max-width: 360px) {
  .jbsm-feat  { flex-direction: column; align-items: flex-start; }
  .jbsm-feat-icon { align-self: flex-start; }
}
`;

/* ─── Mandala SVG ─────────────────────────────────────────────────────────── */
const Mandala = () => (
  <svg
    className="jbsm-mandala"
    viewBox="0 0 400 400"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    {[30, 60, 90, 120, 150, 175, 195].map((r, i) => (
      <circle
        key={i}
        cx="200"
        cy="200"
        r={r}
        stroke="#C9962A"
        strokeWidth={i === 6 ? 1.5 : 0.8}
        strokeDasharray={i === 1 ? "4 4" : undefined}
      />
    ))}
    {Array.from({ length: 16 }, (_, i) => {
      const a = i * 22.5 * (Math.PI / 180);
      const x1 = 200 + 92 * Math.cos(a),
        y1 = 200 + 92 * Math.sin(a);
      const cx1 = 200 + 132 * Math.cos(a + 0.18),
        cy1 = 200 + 132 * Math.sin(a + 0.18);
      const cx2 = 200 + 132 * Math.cos(a - 0.18),
        cy2 = 200 + 132 * Math.sin(a - 0.18);
      return (
        <path
          key={i}
          d={`M200,200 Q${cx1},${cy1} ${x1},${y1} Q${cx2},${cy2} 200,200`}
          stroke="#C9962A"
          strokeWidth="0.7"
        />
      );
    })}
    {Array.from({ length: 12 }, (_, i) => {
      const a = i * 30 * (Math.PI / 180);
      return (
        <line
          key={i}
          x1={200 + 22 * Math.cos(a)}
          y1={200 + 22 * Math.sin(a)}
          x2={200 + 62 * Math.cos(a)}
          y2={200 + 62 * Math.sin(a)}
          stroke="#C9962A"
          strokeWidth="0.8"
        />
      );
    })}
    <circle cx="200" cy="200" r="22" stroke="#C9962A" strokeWidth="1" />
    {Array.from({ length: 8 }, (_, i) => {
      const a = (i * 45 + 22.5) * (Math.PI / 180);
      return (
        <circle
          key={i}
          cx={200 + 168 * Math.cos(a)}
          cy={200 + 168 * Math.sin(a)}
          r="7"
          stroke="#C9962A"
          strokeWidth="0.6"
        />
      );
    })}
  </svg>
);

/* ─── Feature data ────────────────────────────────────────────────────────── */
const features = [
  {
    Icon: FaPrayingHands,
    title: "Value-Based Compatibility",
    desc: "We prioritise shared Jain principles — Ahimsa, Satya, and Aparigraha — to ensure a foundation of lifelong spiritual harmony.",
  },
  {
    Icon: FaHeart,
    title: "Community Centred",
    desc: "Exclusive verified profiles from the Jain community across all sects — Digambar, Shwetambar, and Sthanakvasi.",
  },
  {
    Icon: FaBalanceScale,
    title: "Transparent & Ethical",
    desc: "A holistic approach to matchmaking grounded in complete privacy, Dharmic integrity, and respectful guidance.",
  },
];

const stats = [
  { num: "5000+", lbl: "Jain families connected" },
  { num: "98%", lbl: "Family satisfaction rate" },
  { num: "10×", lbl: "Faster sacred matchmaking" },
];

/* ─── Component ───────────────────────────────────────────────────────────── */
const JainSpiritualMatrimony = () => {
  useEffect(() => {
    const id = "jbsm-styles";
    if (!document.getElementById(id)) {
      const el = document.createElement("style");
      el.id = id;
      el.textContent = CSS;
      document.head.appendChild(el);
    }
    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, []);

  return (
    <section className="jbsm" aria-labelledby="jbsm-title">
      <div className="jbsm-dust" aria-hidden="true" />
      <Mandala />

      <div className="jbsm-inner">
        {/* ══ LEFT ══ */}
        <div className="jbsm-left">
          {/* Brand */}
          <div className="jbsm-brand">
            <img src={jainLogo} alt="Jain Var Vadhu" className="jbsm-logo-ring" />
            <div className="jbsm-brand-text">
              <span className="jbsm-brand-name">Jain Var Vadhu</span>
              <span className="jbsm-brand-tag">
                Divine Values · Sacred Bond
              </span>
            </div>
          </div>

          {/* Eyebrow */}
          <div className="jbsm-eyebrow" aria-hidden="true">
            <span className="jbsm-eline" />
            Jain Spiritual Matchmaking
            <span className="jbsm-eline r" />
          </div>

          {/* Headline */}
          <h2 className="jbsm-headline" id="jbsm-title">
            Connecting souls through
            <br />
            <em>Ahimsa, values &amp;</em>
            <br />
            spiritual harmony
          </h2>

          {/* Opening quote */}
          <blockquote className="jbsm-quote">
            <p>
              "A successful marriage is not just the union of two people, but
              the alignment of two spirits walking the path of Dharma together."
            </p>
            <cite>— Jain Var Vadhu</cite>
          </blockquote>

          {/* Divider */}
          <div className="jbsm-divrow" aria-hidden="true">
            <div className="jbsm-divline" />
            <span className="jbsm-divsym">☸</span>
            <div className="jbsm-divline" />
          </div>

          {/* Features */}
          <div
            className="jbsm-features"
            role="list"
            aria-label="Service highlights"
          >
            {features.map(({ Icon, title, desc }) => (
              <div key={title} className="jbsm-feat" role="listitem">
                <div className="jbsm-feat-icon" aria-hidden="true">
                  <Icon />
                </div>
                <div className="jbsm-feat-body">
                  <div className="jbsm-feat-title">{title}</div>
                  <p className="jbsm-feat-desc">{desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <a href="/contact-us" className="jbsm-cta">
            🪷 Begin Your Journey →
          </a>
        </div>

        {/* ══ RIGHT ══ */}
        <div className="jbsm-right">
          {/* Dharma quote card */}
          <div className="jbsm-dharma-card">
            <div className="jbsm-dharma-om" aria-label="Om">
              ꣽ
            </div>
            <p className="jbsm-dharma-text">
              "Parasparopagraho Jīvānām" — All life is bound together by mutual
              support and interdependence. Let your match begin with this sacred
              truth.
            </p>
            <cite className="jbsm-dharma-attr">Tattvartha Sutra · 5.21</cite>
          </div>

          {/* Stats */}
          <div className="jbsm-stats" aria-label="Platform statistics">
            {stats.map(({ num, lbl }) => (
              <div key={lbl} className="jbsm-stat">
                <div className="jbsm-stat-num">{num}</div>
                <div className="jbsm-stat-lbl">{lbl}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default JainSpiritualMatrimony;
